import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
  ModelInputsBlendState,
  ModelInputSection,
  ModelInputSectionType,
  SelectionContext,
  SectionUpdatePayload,
  MergeResult,
  createEmptyBlendState,
  createEmptySection
} from './model-inputs-state.interface';

type ModelInputReconcileReason =
  | 'vm-selected-change'
  | 'ppd-selected-change'
  | 'base-oil-selected-change'
  | 'packages-change'
  | 'modal-load'
  | 'prediction-build'
  | 'manual';

interface ModelInputReconcileSummary {
  blendNumber: number;
  reason: ModelInputReconcileReason;
  removed: { vm: number; bo: number; di: number };
  totalRemoved: number;
  clearedColumns: string[];
  reconciledAt: string;
}

interface ModelInputConsistencyReport {
  blendNumber: number;
  isConsistent: boolean;
  warnings: string[];
}

@Injectable({
  providedIn: 'root'
})

/**
 * Shared state hub for Blend Calculator.
 *
 * This service contains two coexisting layers:
 * 1) New model-input state architecture (Map by blend + merge/reconcile helpers).
 * 2) Legacy BehaviorSubject-based state migrated from React.
 *
 * Both layers are still used by components during migration, so documentation
 * and naming try to keep backward compatibility while clarifying intent.
 */
export class SharedServiceService {

  constructor() { }

  // #region Model Inputs State Management (New Architecture)
  // =========================================================
  // Map-based state management for N-blend support
  // Each blend has its own BehaviorSubject with complete state
  // =========================================================

  private modelInputsStateMap = new Map<number, BehaviorSubject<ModelInputsBlendState>>();

  /**
   * Get or create the BehaviorSubject for a specific blend
   * Implements lazy creation - state is only created when first accessed
   */
  private getOrCreateBlendState(blendIndex: number): BehaviorSubject<ModelInputsBlendState> {
    if (!this.modelInputsStateMap.has(blendIndex)) {
      const initialState = createEmptyBlendState(blendIndex);
      this.modelInputsStateMap.set(blendIndex, new BehaviorSubject<ModelInputsBlendState>(initialState));
    }
    return this.modelInputsStateMap.get(blendIndex)!;
  }

  /**
   * Get observable for a specific blend's state
   * Use this to subscribe to state changes
   */
  getModelInputsState$(blendIndex: number): Observable<ModelInputsBlendState> {
    return this.getOrCreateBlendState(blendIndex).asObservable();
  }

  /**
   * Get current snapshot of a blend's state (non-reactive)
   */
  getModelInputsStateSnapshot(blendIndex: number): ModelInputsBlendState {
    return this.getOrCreateBlendState(blendIndex).getValue();
  }

  /**
   * Update a specific section's data for a blend
   * This is the primary method for saving Model Inputs data
   */
  updateModelInputsSection(payload: SectionUpdatePayload): void {
    const subject = this.getOrCreateBlendState(payload.blendIndex);
    const currentState = subject.getValue();

    const section = currentState[payload.sectionType];
    const updatedSection: ModelInputSection = {
      ...section,
      data: payload.data ?? section.data,
      headers: payload.headers ?? section.headers,
      savedSelections: payload.savedSelections ?? section.savedSelections,
      isDirty: payload.markAsSaved ? false : true,
      lastSaved: payload.markAsSaved ? new Date() : section.lastSaved
    };

    const updatedState: ModelInputsBlendState = {
      ...currentState,
      [payload.sectionType]: updatedSection,
      isInitialized: true,
      lastModified: new Date()
    };

    subject.next(updatedState);
  }

  /**
   * Save all sections for a blend at once
   * Marks all sections as saved (not dirty)
   */
  saveAllSections(blendIndex: number, sections: {
    vm?: { data: any[]; headers: string[]; selections: SelectionContext[] };
    baseOil?: { data: any[]; headers: string[]; selections: SelectionContext[] };
    ppd?: { data: any[]; headers: string[]; selections: SelectionContext[] };
    additives?: { data: any[]; headers: string[]; selections: SelectionContext[] };
  }): void {
    const subject = this.getOrCreateBlendState(blendIndex);
    const currentState = subject.getValue();
    const now = new Date();

    const createUpdatedSection = (
      current: ModelInputSection,
      update?: { data: any[]; headers: string[]; selections: SelectionContext[] }
    ): ModelInputSection => {
      if (!update) return current;
      return {
        data: update.data,
        headers: update.headers,
        savedSelections: update.selections,
        isDirty: false,
        lastSaved: now
      };
    };

    const updatedState: ModelInputsBlendState = {
      ...currentState,
      vm: createUpdatedSection(currentState.vm, sections.vm),
      baseOil: createUpdatedSection(currentState.baseOil, sections.baseOil),
      ppd: createUpdatedSection(currentState.ppd, sections.ppd),
      additives: createUpdatedSection(currentState.additives, sections.additives),
      isInitialized: true,
      lastModified: now
    };

    subject.next(updatedState);
  }

  /**
   * Check if saved selections match current selections
   * Used to determine if saved data should be restored
   */
  shouldRestoreSection(
    blendIndex: number,
    sectionType: ModelInputSectionType,
    currentSelections: SelectionContext[]
  ): boolean {
    const state = this.getModelInputsStateSnapshot(blendIndex);
    const section = state[sectionType];

    // If never saved, don't restore
    if (!section.lastSaved || section.data.length === 0) {
      return false;
    }

    // If different number of selections, don't restore
    if (section.savedSelections.length !== currentSelections.length) {
      return false;
    }

    // Check each selection by name AND index
    return section.savedSelections.every((saved, i) => {
      const current = currentSelections[i];
      return saved.name === current.name && saved.index === current.index;
    });
  }

  /**
   * Perform smart merge: compare selections and decide what to restore
   * Returns which sections should be restored vs loaded fresh
   */
  evaluateMerge(
    blendIndex: number,
    currentSelections: {
      vm: SelectionContext[];
      baseOil: SelectionContext[];
      ppd: SelectionContext[];
      additives: SelectionContext[];
    }
  ): MergeResult {
    const sectionsToCheck: ModelInputSectionType[] = ['vm', 'baseOil', 'ppd', 'additives'];
    const restored: ModelInputSectionType[] = [];
    const fresh: ModelInputSectionType[] = [];

    for (const sectionType of sectionsToCheck) {
      const shouldRestore = this.shouldRestoreSection(
        blendIndex,
        sectionType,
        currentSelections[sectionType]
      );

      if (shouldRestore) {
        restored.push(sectionType);
      } else {
        fresh.push(sectionType);
      }
    }

    let action: 'restored' | 'fresh' | 'merged';
    if (restored.length === 4) {
      action = 'restored';
    } else if (fresh.length === 4) {
      action = 'fresh';
    } else {
      action = 'merged';
    }

    return {
      action,
      sectionsRestored: restored,
      sectionsFresh: fresh,
      message: action === 'merged'
        ? `Restored: ${restored.join(', ')}. Fresh: ${fresh.join(', ')}.`
        : undefined
    };
  }

  /**
   * Clear saved state for a specific blend
   */
  clearModelInputsState(blendIndex: number): void {
    const subject = this.getOrCreateBlendState(blendIndex);
    subject.next(createEmptyBlendState(blendIndex));
  }

  /**
   * Clear all saved Model Inputs states
   */
  clearAllModelInputsStates(): void {
    this.modelInputsStateMap.forEach((subject, blendIndex) => {
      subject.next(createEmptyBlendState(blendIndex));
    });
  }

  /**
   * Check if a blend has any saved data
   */
  hasModelInputsData(blendIndex: number): boolean {
    if (!this.modelInputsStateMap.has(blendIndex)) {
      return false;
    }
    const state = this.getModelInputsStateSnapshot(blendIndex);
    return state.isInitialized && (
      state.vm.data.length > 0 ||
      state.baseOil.data.length > 0 ||
      state.ppd.data.length > 0 ||
      state.additives.data.length > 0
    );
  }

  /**
   * Get section data for a specific blend and section type
   * Returns null if no data exists
   */
  getSectionData(blendIndex: number, sectionType: ModelInputSectionType): ModelInputSection | null {
    if (!this.modelInputsStateMap.has(blendIndex)) {
      return null;
    }
    const state = this.getModelInputsStateSnapshot(blendIndex);
    const section = state[sectionType];
    return section.lastSaved ? section : null;
  }

  // #endregion Model Inputs State Management

  // BehaviorSubjects for Modification totals
  private totalRatioP1Source = new BehaviorSubject<number>(0);
  private totalTreatP1Source = new BehaviorSubject<number>(0);
  private totalRatioP2Source = new BehaviorSubject<number>(0);
  private totalTreatP2Source = new BehaviorSubject<number>(0);

  // Observable for Modification totals
  totalRatioP1$ = this.totalRatioP1Source.asObservable();
  totalTreatP1$ = this.totalTreatP1Source.asObservable();
  totalRatioP2$ = this.totalRatioP2Source.asObservable();
  totalTreatP2$ = this.totalTreatP2Source.asObservable();

  // #region Legacy BehaviorSubjects (migration from React datastore.js)
  // These streams are consumed by current UI components. Keep public method
  // signatures stable until all callers migrate to the new model-input API.

  private packagesDataSource = new BehaviorSubject<any[]>([]);
  packagesData$ = this.packagesDataSource.asObservable();

  private prediction_valueSource = new BehaviorSubject<any[]>([]);
  prediction_value$ = this.prediction_valueSource.asObservable();

  private package_1_pred_1Source = new BehaviorSubject<any[]>([]);
  package_1_pred_1$ = this.package_1_pred_1Source.asObservable();

  private package_1_pred_2Source = new BehaviorSubject<any[]>([]);
  package_1_pred_2$ = this.package_1_pred_2Source.asObservable();

  private package_2_pred_1Source = new BehaviorSubject<any[]>([]);
  package_2_pred_1$ = this.package_2_pred_1Source.asObservable();

  private package_2_pred_2Source = new BehaviorSubject<any[]>([]);
  package_2_pred_2$ = this.package_2_pred_2Source.asObservable();

  // FIX: Totals are numbers, not arrays
  private total_package_1_pred_1Source = new BehaviorSubject<number>(0);
  total_package_1_pred_1$ = this.total_package_1_pred_1Source.asObservable();

  private total_package_1_pred_2Source = new BehaviorSubject<number>(0);
  total_package_1_pred_2$ = this.total_package_1_pred_2Source.asObservable();

  private total_package_2_pred_1Source = new BehaviorSubject<number>(0);
  total_package_2_pred_1$ = this.total_package_2_pred_1Source.asObservable();

  private total_package_2_pred_2Source = new BehaviorSubject<number>(0);
  total_package_2_pred_2$ = this.total_package_2_pred_2Source.asObservable();

  private total_dil_per_package_1_pred_1Source = new BehaviorSubject<number>(0);
  total_dil_per_package_1_pred_1$ = this.total_dil_per_package_1_pred_1Source.asObservable();

  private total_dil_per_package_1_pred_2Source = new BehaviorSubject<number>(0);
  total_dil_per_package_1_pred_2$ = this.total_dil_per_package_1_pred_2Source.asObservable();

  private total_dil_per_package_2_pred_1Source = new BehaviorSubject<number>(0);
  total_dil_per_package_2_pred_1$ = this.total_dil_per_package_2_pred_1Source.asObservable();

  private total_dil_per_package_2_pred_2Source = new BehaviorSubject<number>(0);
  total_dil_per_package_2_pred_2$ = this.total_dil_per_package_2_pred_2Source.asObservable();

  private package_1_pred_1_update_flagSource = new BehaviorSubject<boolean>(true);
  package_1_pred_1_update_flag$ = this.package_1_pred_1_update_flagSource.asObservable();

  private package_1_pred_2_update_flagSource = new BehaviorSubject<boolean>(true);
  package_1_pred_2_update_flag$ = this.package_1_pred_2_update_flagSource.asObservable();

  private package_2_pred_1_update_flagSource = new BehaviorSubject<boolean>(true);
  package_2_pred_1_update_flag$ = this.package_2_pred_1_update_flagSource.asObservable();

  private package_2_pred_2_update_flagSource = new BehaviorSubject<boolean>(true);
  package_2_pred_2_update_flag$ = this.package_2_pred_2_update_flagSource.asObservable();

  private modelinput1_vm_dataSource = new BehaviorSubject<any[]>([]);
  modelinput1_vm_data$ = this.modelinput1_vm_dataSource.asObservable();

  private modelinput2_vm_dataSource = new BehaviorSubject<any[]>([]);
  modelinput2_vm_data$ = this.modelinput2_vm_dataSource.asObservable();

  private modelinputs_pred_inputsSource = new BehaviorSubject<any[]>([]);
  modelinputs_pred_inputs$ = this.modelinputs_pred_inputsSource.asObservable();

  private finishedoil_dataSource = new BehaviorSubject<any[]>([]);
  finishedoil_data$ = this.finishedoil_dataSource.asObservable();

  private ImpFavoriteFlagSource = new BehaviorSubject<boolean>(false);
  ImpFavoriteFlag$ = this.ImpFavoriteFlagSource.asObservable();

  // True once the blend-calculator dropdown data (getdropdown) has settled so the
  // modification panels are mounted. Used to gate shared-favorite loading in new tabs.
  private blendCalculatorReadySource = new BehaviorSubject<boolean>(false);
  blendCalculatorReady$ = this.blendCalculatorReadySource.asObservable();

  private importedFavoriteNameSource = new BehaviorSubject<string | null>(null);
  importedFavoriteName$ = this.importedFavoriteNameSource.asObservable();

  private importedFavoriteSlotSource = new BehaviorSubject<string | null>(null);
  importedFavoriteSlot$ = this.importedFavoriteSlotSource.asObservable();

  private importedFavoriteFilenameSource = new BehaviorSubject<string | null>(null);
  importedFavoriteFilename$ = this.importedFavoriteFilenameSource.asObservable();

  private shareFavoriteFlagSource = new BehaviorSubject<boolean>(false);
  shareFavoriteFlag$ = this.shareFavoriteFlagSource.asObservable();

  private total_treat_ratio_p1Source = new BehaviorSubject<any[]>([]);
  total_treat_ratio_p1$ = this.total_treat_ratio_p1Source.asObservable();

  private total_treat_wt_p1Source = new BehaviorSubject<any[]>([]);
  total_treat_wt_p1$ = this.total_treat_wt_p1Source.asObservable();

  private total_treat_ratio_p2Source = new BehaviorSubject<any[]>([]);
  total_treat_ratio_p2$ = this.total_treat_ratio_p2Source.asObservable();

  private pred_model_input_1Source = new BehaviorSubject<any[]>([]);
  pred_model_input_1$ = this.pred_model_input_1Source.asObservable();

  private pred_model_input_2Source = new BehaviorSubject<any[]>([]);
  pred_model_input_2$ = this.pred_model_input_2Source.asObservable();

  private mod_inp_diSource = new BehaviorSubject<any[]>([]);
  mod_inp_di$ = this.mod_inp_diSource.asObservable();

  private mod_inp_vmSource = new BehaviorSubject<any[]>([]);
  mod_inp_vm$ = this.mod_inp_vmSource.asObservable();

  private mod_inp_ppdSource = new BehaviorSubject<any[]>([]);
  mod_inp_ppd$ = this.mod_inp_ppdSource.asObservable();

  private mod_inp_boSource = new BehaviorSubject<any[]>([]);
  mod_inp_bo$ = this.mod_inp_boSource.asObservable();

  private mod_inp_di2Source = new BehaviorSubject<any[]>([]);
  mod_inp_di2$ = this.mod_inp_di2Source.asObservable();

  private mod_inp_vm2Source = new BehaviorSubject<any[]>([]);
  mod_inp_vm2$ = this.mod_inp_vm2Source.asObservable();

  private mod_inp_ppd2Source = new BehaviorSubject<any[]>([]);
  mod_inp_ppd2$ = this.mod_inp_ppd2Source.asObservable();

  private mod_inp_bo2Source = new BehaviorSubject<any[]>([]);
  mod_inp_bo2$ = this.mod_inp_bo2Source.asObservable();

  private mod_inp_col_vmSource = new BehaviorSubject<any[]>([]);
  mod_inp_col_vm$ = this.mod_inp_col_vmSource.asObservable();

  private mod_inp_col_diSource = new BehaviorSubject<any[]>([]);
  mod_inp_col_di$ = this.mod_inp_col_diSource.asObservable();

  private mod_inp_col_boSource = new BehaviorSubject<any[]>([]);
  mod_inp_col_bo$ = this.mod_inp_col_boSource.asObservable();

  private mod_inp_col_vm2Source = new BehaviorSubject<any[]>([]);
  mod_inp_col_vm2$ = this.mod_inp_col_vm2Source.asObservable();

  private mod_inp_col_di2Source = new BehaviorSubject<any[]>([]);
  mod_inp_col_di2$ = this.mod_inp_col_di2Source.asObservable();

  private mod_inp_col_bo2Source = new BehaviorSubject<any[]>([]);
  mod_inp_col_bo2$ = this.mod_inp_col_bo2Source.asObservable();

  private clear_FO_flagSource = new BehaviorSubject<boolean>(false);
  clear_FO_flag$ = this.clear_FO_flagSource.asObservable();

  private fo_dill_percent_pack1_p1Source = new BehaviorSubject<any[]>([]);
  fo_dill_percent_pack1_p1$ = this.fo_dill_percent_pack1_p1Source.asObservable();

  private fo_dill_percent_pack1_p2Source = new BehaviorSubject<any[]>([]);
  fo_dill_percent_pack1_p2$ = this.fo_dill_percent_pack1_p2Source.asObservable();

  private fo_dill_percent_pack2_p1Source = new BehaviorSubject<any[]>([]);
  fo_dill_percent_pack2_p1$ = this.fo_dill_percent_pack2_p1Source.asObservable();

  private fo_dill_percent_pack2_p2Source = new BehaviorSubject<any[]>([]);
  fo_dill_percent_pack2_p2$ = this.fo_dill_percent_pack2_p2Source.asObservable();

  private user_mail_idSource = new BehaviorSubject<string>('');
  user_mail_id$ = this.user_mail_idSource.asObservable();

  private current_timeSource = new BehaviorSubject<any[]>([]);
  current_time$ = this.current_timeSource.asObservable();

  private checklist_dataSource = new BehaviorSubject<string>('');
  checklist_data$ = this.checklist_dataSource.asObservable();

  private Dil_wt_percent_Pack1_pred1Source = new BehaviorSubject<string>('');
  Dil_wt_percent_Pack1_pred1$ = this.Dil_wt_percent_Pack1_pred1Source.asObservable();

  private Dil_wt_percent_Pack1_pred2Source = new BehaviorSubject<string>('');
  Dil_wt_percent_Pack1_pred2$ = this.Dil_wt_percent_Pack1_pred2Source.asObservable();

  private Dil_wt_percent_Pack2_pred1Source = new BehaviorSubject<string>('');
  Dil_wt_percent_Pack2_pred1$ = this.Dil_wt_percent_Pack2_pred1Source.asObservable();

  private Dil_wt_percent_Pack2_pred2Source = new BehaviorSubject<string>('');
  Dil_wt_percent_Pack2_pred2$ = this.Dil_wt_percent_Pack2_pred2Source.asObservable();

  private prediction_resultsSource = new BehaviorSubject<any[]>([]);
  prediction_results$ = this.prediction_resultsSource.asObservable();
  hasPredictions$ = this.prediction_results$.pipe(
    map(results => Array.isArray(results) && results.length > 0)
  );

  private PackFlagSource = new BehaviorSubject<boolean>(false);
  PackFlag$ = this.PackFlagSource.asObservable();

  private update_packageTable_dataSource = new BehaviorSubject<any[]>([]);
  update_packageTable_data$ = this.update_packageTable_dataSource.asObservable();

  private modelInputs1Source = new BehaviorSubject<any[]>([]);
  modelInputs1$ = this.modelInputs1Source.asObservable();

  private modelInputs2Source = new BehaviorSubject<any[]>([]);
  modelInputs2$ = this.modelInputs2Source.asObservable();

  private modelInputReconcileStatsSource = new BehaviorSubject<{
    blend1: number;
    blend2: number;
    lastBlend: number;
    lastReason: string;
    lastRunAt: string;
  }>({
    blend1: 0,
    blend2: 0,
    lastBlend: 0,
    lastReason: '',
    lastRunAt: ''
  });
  modelInputReconcileStats$ = this.modelInputReconcileStatsSource.asObservable();

  setModelInputs1(data: any[]): void {
    this.modelInputs1Source.next(data);
  }

  setModelInputs2(data: any[]): void {
    this.modelInputs2Source.next(data);
  }

  set_pred_model_input_1(data: any) {
    this.pred_model_input_1Source.next(data);
  }

  set_pred_model_input_2(data: any) {
    this.pred_model_input_2Source.next(data);
  }

  setmodelinputs_pred_inputs(data: any) {
    this.modelinputs_pred_inputsSource.next(data);
  }

  setmodelinput1_vm_data(data: any) {
    this.modelinput1_vm_dataSource.next(data);
  }

  setmodelinput2_vm_data(data: any) {
    this.modelinput2_vm_dataSource.next(data);
  }

  setPackage1Pred1Flag(data: any) {
    this.package_1_pred_1_update_flagSource.next(data);
  }

  setPackage1Pred2Flag(data: any) {
    this.package_1_pred_2_update_flagSource.next(data);
  }

  setPackage2Pred1Flag(data: any) {
    this.package_2_pred_1_update_flagSource.next(data);
  }

  setPackage2Pred2Flag(data: any) {
    this.package_2_pred_2_update_flagSource.next(data);
  }

  private normalizeText(value: any): string {
    return String(value ?? '').trim();
  }

  private toPositiveNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private hasBlendSelection(value: any): boolean {
    return this.toPositiveNumber(value) > 0;
  }

  private buildBaseOilKey(name: any, batch: any): string {
    return `${this.normalizeText(name)}::${this.normalizeText(batch)}`;
  }

  private extractVmName(row: any): string {
    return this.normalizeText(row?.VM ?? row?.name ?? row?.selectedValue);
  }

  private extractBaseOilKey(row: any): string {
    return this.buildBaseOilKey(
      row?.['Base Oil'] ?? row?.name ?? row?.oil,
      row?.Batch ?? row?.batch
    );
  }

  private extractDiName(row: any): string {
    return this.normalizeText(row?.Additive ?? row?.name ?? row?.component);
  }

  private getSelectedVmNamesForBlend(blendNumber: number): Set<string> {
    const treatKey = blendNumber === 1 ? 'treat1' : 'treat2';
    const selected = new Set<string>();

    (this.vmSelectedDataSource.getValue() || []).forEach((row: any) => {
      const vmName = this.extractVmName(row);
      if (!vmName) {
        return;
      }
      if (this.hasBlendSelection(row?.[treatKey])) {
        selected.add(vmName);
      }
    });

    return selected;
  }

  private getSelectedBaseOilKeysForBlend(blendNumber: number): Set<string> {
    const ratioKey = blendNumber === 1 ? 'ratio1' : 'ratio2';
    const selected = new Set<string>();

    (this.baseOilSelectedDataSource.getValue() || []).forEach((row: any) => {
      const name = this.normalizeText(row?.oil ?? row?.name ?? row?.['Base Oil']);
      const batch = this.normalizeText(row?.batch ?? row?.Batch);
      if (!name || !batch) {
        return;
      }
      if (this.hasBlendSelection(row?.[ratioKey])) {
        selected.add(this.buildBaseOilKey(name, batch));
      }
    });

    return selected;
  }

  private getSelectedDiNamesForBlend(blendNumber: number): Set<string> {
    const blendIndex = blendNumber - 1;
    const selected = new Set<string>();

    (this.packagesDataSource.getValue() || []).forEach((pkg: any) => {
      const blendRows = Array.isArray(pkg) ? pkg[blendIndex] : null;
      if (!Array.isArray(blendRows)) {
        return;
      }

      blendRows.forEach((row: any) => {
        const additiveName = this.extractDiName(row);
        if (additiveName) {
          selected.add(additiveName);
        }
      });
    });

    return selected;
  }

  private clearColumnsIfRowsEmpty(rows: any[], columnSubject: BehaviorSubject<any[]>): boolean {
    if (rows.length > 0) {
      return false;
    }

    const currentColumns = columnSubject.getValue() || [];
    if (currentColumns.length === 0) {
      return false;
    }

    columnSubject.next([]);
    return true;
  }

  private registerReconcileRun(blendNumber: number, reason: ModelInputReconcileReason): void {
    const currentStats = this.modelInputReconcileStatsSource.getValue();
    const nextStats = {
      blend1: blendNumber === 1 ? currentStats.blend1 + 1 : currentStats.blend1,
      blend2: blendNumber === 2 ? currentStats.blend2 + 1 : currentStats.blend2,
      lastBlend: blendNumber,
      lastReason: reason,
      lastRunAt: new Date().toISOString()
    };
    this.modelInputReconcileStatsSource.next(nextStats);
  }

  reconcileModelInputsForBlend(
    blendNumber: number,
    reason: ModelInputReconcileReason = 'manual'
  ): ModelInputReconcileSummary {
    if (blendNumber !== 1 && blendNumber !== 2) {
      return {
        blendNumber,
        reason,
        removed: { vm: 0, bo: 0, di: 0 },
        totalRemoved: 0,
        clearedColumns: [],
        reconciledAt: new Date().toISOString()
      };
    }

    // Imported model inputs are authoritative; skip pruning until the user changes this blend's selections.
    if (this.getModelInputsImported(blendNumber)) {
      return {
        blendNumber,
        reason,
        removed: { vm: 0, bo: 0, di: 0 },
        totalRemoved: 0,
        clearedColumns: [],
        reconciledAt: new Date().toISOString()
      };
    }

    const vmRowsSubject = blendNumber === 1 ? this.mod_inp_vmSource : this.mod_inp_vm2Source;
    const boRowsSubject = blendNumber === 1 ? this.mod_inp_boSource : this.mod_inp_bo2Source;
    const diRowsSubject = blendNumber === 1 ? this.mod_inp_diSource : this.mod_inp_di2Source;

    const vmColsSubject = blendNumber === 1 ? this.mod_inp_col_vmSource : this.mod_inp_col_vm2Source;
    const boColsSubject = blendNumber === 1 ? this.mod_inp_col_boSource : this.mod_inp_col_bo2Source;
    const diColsSubject = blendNumber === 1 ? this.mod_inp_col_diSource : this.mod_inp_col_di2Source;

    const selectedVmNames = this.getSelectedVmNamesForBlend(blendNumber);
    const selectedBoKeys = this.getSelectedBaseOilKeysForBlend(blendNumber);
    const selectedDiNames = this.getSelectedDiNamesForBlend(blendNumber);

    const currentVmRows = vmRowsSubject.getValue() || [];
    const currentBoRows = boRowsSubject.getValue() || [];
    const currentDiRows = diRowsSubject.getValue() || [];

    const nextVmRows = currentVmRows.filter((row: any) => selectedVmNames.has(this.extractVmName(row)));
    const nextBoRows = currentBoRows.filter((row: any) => selectedBoKeys.has(this.extractBaseOilKey(row)));
    const nextDiRows = currentDiRows.filter((row: any) => selectedDiNames.has(this.extractDiName(row)));

    if (nextVmRows.length !== currentVmRows.length) {
      vmRowsSubject.next(nextVmRows);
    }
    if (nextBoRows.length !== currentBoRows.length) {
      boRowsSubject.next(nextBoRows);
    }
    if (nextDiRows.length !== currentDiRows.length) {
      diRowsSubject.next(nextDiRows);
    }

    const clearedColumns: string[] = [];
    if (this.clearColumnsIfRowsEmpty(nextVmRows, vmColsSubject)) {
      clearedColumns.push(blendNumber === 1 ? 'mod_inp_col_vm' : 'mod_inp_col_vm2');
    }
    if (this.clearColumnsIfRowsEmpty(nextBoRows, boColsSubject)) {
      clearedColumns.push(blendNumber === 1 ? 'mod_inp_col_bo' : 'mod_inp_col_bo2');
    }
    if (this.clearColumnsIfRowsEmpty(nextDiRows, diColsSubject)) {
      clearedColumns.push(blendNumber === 1 ? 'mod_inp_col_di' : 'mod_inp_col_di2');
    }

    const removed = {
      vm: Math.max(0, currentVmRows.length - nextVmRows.length),
      bo: Math.max(0, currentBoRows.length - nextBoRows.length),
      di: Math.max(0, currentDiRows.length - nextDiRows.length)
    };

    this.registerReconcileRun(blendNumber, reason);

    return {
      blendNumber,
      reason,
      removed,
      totalRemoved: removed.vm + removed.bo + removed.di,
      clearedColumns,
      reconciledAt: new Date().toISOString()
    };
  }

  reconcileModelInputsForAllBlends(reason: ModelInputReconcileReason = 'manual'): ModelInputReconcileSummary[] {
    return [
      this.reconcileModelInputsForBlend(1, reason),
      this.reconcileModelInputsForBlend(2, reason)
    ];
  }

  validateModelInputsConsistencyForBlend(blendNumber: number): ModelInputConsistencyReport {
    if (blendNumber !== 1 && blendNumber !== 2) {
      return {
        blendNumber,
        isConsistent: false,
        warnings: [`Unsupported blend number ${blendNumber}`]
      };
    }

    const warnings: string[] = [];

    const vmRows = blendNumber === 1 ? this.mod_inp_vmSource.getValue() || [] : this.mod_inp_vm2Source.getValue() || [];
    const boRows = blendNumber === 1 ? this.mod_inp_boSource.getValue() || [] : this.mod_inp_bo2Source.getValue() || [];
    const diRows = blendNumber === 1 ? this.mod_inp_diSource.getValue() || [] : this.mod_inp_di2Source.getValue() || [];

    const vmCols = blendNumber === 1 ? this.mod_inp_col_vmSource.getValue() || [] : this.mod_inp_col_vm2Source.getValue() || [];
    const boCols = blendNumber === 1 ? this.mod_inp_col_boSource.getValue() || [] : this.mod_inp_col_bo2Source.getValue() || [];
    const diCols = blendNumber === 1 ? this.mod_inp_col_diSource.getValue() || [] : this.mod_inp_col_di2Source.getValue() || [];

    const selectedVmNames = this.getSelectedVmNamesForBlend(blendNumber);
    const selectedBoKeys = this.getSelectedBaseOilKeysForBlend(blendNumber);
    const selectedDiNames = this.getSelectedDiNamesForBlend(blendNumber);

    vmRows.forEach((row: any) => {
      const name = this.extractVmName(row);
      if (name && !selectedVmNames.has(name)) {
        warnings.push(`Orphan VM override in blend ${blendNumber}: ${name}`);
      }
    });

    boRows.forEach((row: any) => {
      const key = this.extractBaseOilKey(row);
      if (key && !selectedBoKeys.has(key)) {
        warnings.push(`Orphan Base Oil override in blend ${blendNumber}: ${key}`);
      }
    });

    diRows.forEach((row: any) => {
      const name = this.extractDiName(row);
      if (name && !selectedDiNames.has(name)) {
        warnings.push(`Orphan DI override in blend ${blendNumber}: ${name}`);
      }
    });

    if (vmRows.length === 0 && vmCols.length > 0) {
      warnings.push(`Columns present without VM data in blend ${blendNumber}`);
    }
    if (boRows.length === 0 && boCols.length > 0) {
      warnings.push(`Columns present without Base Oil data in blend ${blendNumber}`);
    }
    if (diRows.length === 0 && diCols.length > 0) {
      warnings.push(`Columns present without DI data in blend ${blendNumber}`);
    }

    return {
      blendNumber,
      isConsistent: warnings.length === 0,
      warnings
    };
  }

  setpackagedata(data: any) {
    this.packagesDataSource.next(data);
    this.reconcileModelInputsForAllBlends('packages-change');
  }

  setpredictionvaluedata(data: any) {
    this.prediction_valueSource.next(data);
  }

  setPackage1Pred1(data: any) {
    this.package_1_pred_1Source.next(data);
  }

  setPackage1Pred2(data: any) {
    this.package_1_pred_2Source.next(data);
  }

  setPackage2Pred1(data: any) {
    this.package_2_pred_1Source.next(data);
  }

  setPackage2Pred2(data: any) {
    this.package_2_pred_2Source.next(data);
  }

  settotal_package_1_pred_1(data: any) {
    this.total_package_1_pred_1Source.next(data);
  }

  settotal_package_1_pred_2(data: any) {
    this.total_package_1_pred_2Source.next(data);
  }

  settotal_package_2_pred_1(data: any) {
    this.total_package_2_pred_1Source.next(data);
  }

  settotal_package_2_pred_2(data: any) {
    this.total_package_2_pred_2Source.next(data);
  }

  settotal_dil_per_package_1_pred_1(data: any) {
    this.total_dil_per_package_1_pred_1Source.next(data);
  }

  settotal_dil_per_package_1_pred_2(data: any) {
    this.total_dil_per_package_1_pred_2Source.next(data);
  }

  settotal_dil_per_package_2_pred_1(data: any) {
    this.total_dil_per_package_2_pred_1Source.next(data);
  }

  settotal_dil_per_package_2_pred_2(data: any) {
    this.total_dil_per_package_2_pred_2Source.next(data);
  }

  set_finishedoil_data(data: any) {
    this.finishedoil_dataSource.next(data);
  }

  set_total_treat_ratio_p1(data: any) {
    this.total_treat_ratio_p1Source.next(data);
  }

  set_total_treat_wt_p1(data: any) {
    this.total_treat_wt_p1Source.next(data);
  }

  set_total_treat_ratio_p2(data: any) {
    this.total_treat_ratio_p2Source.next(data);
  }

  set_mod_inp_di(data: any) {
    this.mod_inp_diSource.next(data);
  }

  set_mod_inp_vm(data: any) {
    this.mod_inp_vmSource.next(data);
  }

  set_mod_inp_ppd(data: any) {
    this.mod_inp_ppdSource.next(data);
  }

  set_mod_inp_bo(data: any) {
    this.mod_inp_boSource.next(data);
  }

  set_mod_inp_di2(data: any) {
    this.mod_inp_di2Source.next(data);
  }

  set_mod_inp_vm2(data: any) {
    this.mod_inp_vm2Source.next(data);
  }

  set_mod_inp_ppd2(data: any) {
    this.mod_inp_ppd2Source.next(data);
  }

  set_mod_inp_bo2(data: any) {
    this.mod_inp_bo2Source.next(data);
  }

  // getValue methods for synchronous access to saved Model Inputs
  get_mod_inp_vm_value(): any[] {
    return this.mod_inp_vmSource.getValue();
  }

  get_mod_inp_bo_value(): any[] {
    return this.mod_inp_boSource.getValue();
  }

  get_mod_inp_di_value(): any[] {
    return this.mod_inp_diSource.getValue();
  }

  get_mod_inp_vm2_value(): any[] {
    return this.mod_inp_vm2Source.getValue();
  }

  get_mod_inp_bo2_value(): any[] {
    return this.mod_inp_bo2Source.getValue();
  }

  get_mod_inp_di2_value(): any[] {
    return this.mod_inp_di2Source.getValue();
  }

  // Synchronous access to the current main-screen selections
  get_vmSelectedData_value(): any[] {
    return this.vmSelectedDataSource.getValue() || [];
  }

  get_baseOilSelectedData_value(): any[] {
    return this.baseOilSelectedDataSource.getValue() || [];
  }

  get_packagesData_value(): any[] {
    return this.packagesDataSource.getValue() || [];
  }

  /**
   * Get dilution percentage for a specific package and blend
   * Package 0 = Package 1, Blend 0 = Pred 1, Blend 1 = Pred 2
   */
  getTotalDilPerPackage(packageIndex: number, blendIndex: number): number {
    if (packageIndex === 0) {
      if (blendIndex === 0) {
        return this.total_dil_per_package_1_pred_1Source.getValue();
      } else {
        return this.total_dil_per_package_1_pred_2Source.getValue();
      }
    } else {
      if (blendIndex === 0) {
        return this.total_dil_per_package_2_pred_1Source.getValue();
      } else {
        return this.total_dil_per_package_2_pred_2Source.getValue();
      }
    }
  }

  /**
   * Generates Model Inputs columns dynamically from the data.
   * Backend expects: [{ header: 'ColumnName' }, ...]
   * Columns are extracted from the properties of the first object in each array.
   * This replicates the behavior from React (Object.keys(data[0])).
   * @param vmData VM data array
   * @param boData Base Oil data array
   * @param diData DI (Additives) data array
   */
  getModelInputColumnsFromData(vmData: any[], boData: any[], diData: any[]): { vm: any[], bo: any[], di: any[] } {
    const extractColumns = (data: any[]): any[] => {
      if (!data || data.length === 0) return [];
      return Object.keys(data[0]).map(key => ({ header: key }));
    };

    return {
      vm: extractColumns(vmData),
      bo: extractColumns(boData),
      di: extractColumns(diData)
    };
  }

  set_mod_inp_col_vm(data: any) {
    this.mod_inp_col_vmSource.next(data);
  }

  set_mod_inp_col_di(data: any) {
    this.mod_inp_col_diSource.next(data);
  }

  set_mod_inp_col_bo(data: any) {
    this.mod_inp_col_boSource.next(data);
  }

  set_mod_inp_col_vm2(data: any) {
    this.mod_inp_col_vm2Source.next(data);
  }

  set_mod_inp_col_di2(data: any) {
    this.mod_inp_col_di2Source.next(data);
  }

  set_mod_inp_col_bo2(data: any) {
    this.mod_inp_col_bo2Source.next(data);
  }

  set_clear_FO_flag(data: any) {
    this.clear_FO_flagSource.next(data);
  }

  set_fo_dill_percent_pack1_p1(data: any) {
    this.fo_dill_percent_pack1_p1Source.next(data);
  }

  set_fo_dill_percent_pack1_p2(data: any) {
    this.fo_dill_percent_pack1_p2Source.next(data);
  }

  set_fo_dill_percent_pack2_p1(data: any) {
    this.fo_dill_percent_pack2_p1Source.next(data);
  }

  set_fo_dill_percent_pack2_p2(data: any) {
    this.fo_dill_percent_pack2_p2Source.next(data);
  }

  set_user_mail_id(data: any) {
    this.user_mail_idSource.next(data);
  }

  set_current_time(data: any) {
    this.current_timeSource.next(data);
  }

  set_checklist_data(data: any) {
    this.checklist_dataSource.next(data);
  }

  set_Dil_wt_percent_Pack1_pred1(data: any) {
    this.Dil_wt_percent_Pack1_pred1Source.next(data);
  }

  set_Dil_wt_percent_Pack1_pred2(data: any) {
    this.Dil_wt_percent_Pack1_pred2Source.next(data);
  }

  set_Dil_wt_percent_Pack2_pred1(data: any) {
    this.Dil_wt_percent_Pack2_pred1Source.next(data);
  }

  set_Dil_wt_percent_Pack2_pred2(data: any) {
    this.Dil_wt_percent_Pack2_pred2Source.next(data);
  }

  set_prediction_results(data: any) {
    this.prediction_resultsSource.next(data);
  }

  setPackFlag(data: any) {
    this.PackFlagSource.next(data);
  }

  setImpFavoriteFlag(data: any) {
    this.ImpFavoriteFlagSource.next(data);
  }

  setBlendCalculatorReady(ready: boolean) {
    this.blendCalculatorReadySource.next(ready);
  }

  getBlendCalculatorReady(): boolean {
    return this.blendCalculatorReadySource.value;
  }

  setImportedFavoriteName(name: string | null) {
    this.importedFavoriteNameSource.next(name);
  }

  setImportedFavoriteSlot(slot: string | null) {
    this.importedFavoriteSlotSource.next(slot);
  }

  setImportedFavoriteFilename(filename: string | null) {
    this.importedFavoriteFilenameSource.next(filename);
  }

  setshareFavoriteFlag(data: any) {
    this.shareFavoriteFlagSource.next(data);
  }

  set_packageTable_data(data: any) {
    this.update_packageTable_dataSource.next(data);
  }
  // #endregion Legacy BehaviorSubjects

  private retrievedPackageDataSource = new BehaviorSubject<any[]>([]);
  retrievedPackageData$ = this.retrievedPackageDataSource.asObservable();

  private retrievedPackageIndexSource = new BehaviorSubject<number>(0);
  retrievedPackageIndex$ = this.retrievedPackageIndexSource.asObservable();

  private isHydratingFavoritePackagesSource = new BehaviorSubject<boolean>(false);
  isHydratingFavoritePackages$ = this.isHydratingFavoritePackagesSource.asObservable();

  getIsHydratingFavoritePackages(): boolean {
    return this.isHydratingFavoritePackagesSource.getValue();
  }

  setIsHydratingFavoritePackages(value: boolean): void {
    this.isHydratingFavoritePackagesSource.next(value);
  }

  // Marks imported model inputs authoritative per blend until the user changes that blend's selections.
  private modelInputsImportedSource = new BehaviorSubject<{ 1: boolean; 2: boolean }>({ 1: false, 2: false });

  getModelInputsImported(blendNumber: number): boolean {
    const state = this.modelInputsImportedSource.getValue();
    return blendNumber === 1 ? state[1] : blendNumber === 2 ? state[2] : false;
  }

  setModelInputsImported(blendNumber: number, value: boolean): void {
    const current = this.modelInputsImportedSource.getValue();
    if (blendNumber === 1) {
      this.modelInputsImportedSource.next({ ...current, 1: value });
    } else if (blendNumber === 2) {
      this.modelInputsImportedSource.next({ ...current, 2: value });
    }
  }

  private retrievedPackageBlendUpdateSource = new BehaviorSubject<{
    packageIndex: number;
    blendIndex: number;
    rows: any[];
    requestedTreat?: number;
  } | null>(null);
  retrievedPackageBlendUpdate$ = this.retrievedPackageBlendUpdateSource.asObservable();

  private vmSelectedDataSource = new BehaviorSubject<any[]>([]);
  vmSelectedData$ = this.vmSelectedDataSource.asObservable();

  private ppdSelectedDataSource = new BehaviorSubject<any[]>([]);
  ppdSelectedData$ = this.ppdSelectedDataSource.asObservable();

  private baseOilSelectedDataSource = new BehaviorSubject<any[]>([]);
  baseOilSelectedData$ = this.baseOilSelectedDataSource.asObservable();

  // New BehaviorSubject for treatRate changes
  private treatRateChangeSource = new BehaviorSubject<{
    packageIndex: number;
    blendIndex: number;
    componentIndex: number;
    newValue: number;
  }>({ packageIndex: 0, blendIndex: 0, componentIndex: 0, newValue: 0 });

  treatRateChange$ = this.treatRateChangeSource.asObservable();

  // Method to notify changes in treatRate
  updateTreatRate(packageIndex: number, blendIndex: number, componentIndex: number, newValue: number): void {
    this.treatRateChangeSource.next({
      packageIndex,
      blendIndex,
      componentIndex,
      newValue
    });
  }

  setRetrievedPackageData(data: any[]) {
    this.retrievedPackageDataSource.next(data);
  }

  setRetrievedPackageIndex(index: number) {
    this.retrievedPackageIndexSource.next(index);
  }

  setRetrievedPackageBlendUpdate(packageIndex: number, blendIndex: number, rows: any[], requestedTreat?: number) {
    this.retrievedPackageBlendUpdateSource.next({
      packageIndex,
      blendIndex,
      rows,
      requestedTreat
    });
  }

  setVmSelectedData(data: any[]) {
    this.vmSelectedDataSource.next(data);
    this.reconcileModelInputsForAllBlends('vm-selected-change');
  }

  setPpdSelectedData(data: any[]) {
    this.ppdSelectedDataSource.next(data);
    this.reconcileModelInputsForAllBlends('ppd-selected-change');
  }

  setBaseOilSelectedData(data: any[]) {
    this.baseOilSelectedDataSource.next(data);
    this.reconcileModelInputsForAllBlends('base-oil-selected-change');
  }

  getPpdSelectedData(): any[] {
    return this.ppdSelectedDataSource.getValue();
  }

  // #region Aggregated totals (cross-component validation)

  // Store totals per component
  private componentTotals: Map<string, { ratioP1: number, treatP1: number, ratioP2: number, treatP2: number }> =
    new Map();

  // Recalculate all totals
  private recalculateTotals() {
    let totalRatioP1 = 0;
    let totalTreatP1 = 0;
    let totalRatioP2 = 0;
    let totalTreatP2 = 0;

    this.componentTotals.forEach(totals => {
      totalRatioP1 += isNaN(totals.ratioP1) ? 0 : totals.ratioP1;
      totalTreatP1 += isNaN(totals.treatP1) ? 0 : totals.treatP1;
      totalRatioP2 += isNaN(totals.ratioP2) ? 0 : totals.ratioP2;
      totalTreatP2 += isNaN(totals.treatP2) ? 0 : totals.treatP2;
    });

    this.totalRatioP1Source.next(parseFloat(totalRatioP1.toFixed(2)));
    this.totalTreatP1Source.next(parseFloat(totalTreatP1.toFixed(2)));
    this.totalRatioP2Source.next(parseFloat(totalRatioP2.toFixed(2)));
    this.totalTreatP2Source.next(parseFloat(totalTreatP2.toFixed(2)));
  }

  // Methods to update partial totals
  updateComponentTotals(component: string, treatP1: number, treatP2: number, ratioP1?: number, ratioP2?: number) {
    // Existing values for debugging
    const existingComponent = this.componentTotals.get(component);

    // Store totals per component in a private map
    const finalRatioP1 = ratioP1 ?? 0;
    const finalRatioP2 = ratioP2 ?? 0;
    this.componentTotals.set(component, { ratioP1: finalRatioP1, treatP1, ratioP2: finalRatioP2, treatP2 });
    this.recalculateTotals();

    // Emit an update so other components can react
    this.componentsUpdated.next({ component, action: 'update' });
  }

  // Add this property in the service class
  private componentsUpdated = new BehaviorSubject<any>({ component: '', action: 'update' });

  // This method must exist or be added to the service
  getAllComponentTotals(): any {
    return {
      baseOil: this.componentTotals.get('baseOil') || { treatP1: 0, treatP2: 0, ratioP1: 0, ratioP2: 0 },
      package: this.componentTotals.get('package') || { treatP1: 0, treatP2: 0 },
      vm: this.componentTotals.get('vm') || { treatP1: 0, treatP2: 0 },
      ppd: this.componentTotals.get('ppd') || { treatP1: 0, treatP2: 0 },
      additives: this.componentTotals.get('additives') || { treatP1: 0, treatP2: 0 }
    };
  }

  // We also need a method to observe changes in the totals
  getTotalsUpdates(): Observable<any> {
    return this.componentsUpdated.asObservable();
  }

  // #endregion
}
